import { useMemo, useState } from 'react';
import type { AppointmentSeries, EditSeriesResult, Service, Staff } from '@salonomy/types';
import { Button, Field, Modal, Pill, Select, TextArea, TextInput } from '@salonomy/ui';
import { useEditSeries } from '../../data/recurring.js';
import { serviceSpanMin } from '../../data/services.js';
import { money } from '../../lib/format.js';
import { isoDateInZone, wallMinutes, wallMinutesToUtcIso } from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';
import { MAX_INTERVAL_WEEKS, MAX_OCCURRENCES, seriesSummary } from './cadence.js';
import { SeriesResultPanel } from './SeriesResultPanel.js';

function parseTime(value: string): number | null {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(value);
  if (!m) return null;
  return +m[1]! * 60 + +m[2]!;
}

const INTERVAL_PRESETS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Every week' },
  { value: 2, label: 'Every 2 weeks (fortnightly)' },
  { value: 3, label: 'Every 3 weeks' },
  { value: 4, label: 'Every 4 weeks' },
];

/**
 * WS7 phase 3 — "edit the whole series" (apply forward). Changing staff / service / time-of-day /
 * cadence regenerates every FUTURE, not-yet-started occurrence against the diary; past and in-progress
 * occurrences are never touched. The server returns the re-fitted occurrences plus any that couldn't
 * be re-fitted — surfaced via SeriesResultPanel, never silently dropped.
 *
 * "Edit this one" is a DIFFERENT operation (detach a single occurrence, then edit it as a normal
 * appointment) — handled by useDetachOccurrence from the diary's occurrence flow, not here.
 */
export function EditSeriesModal({
  series,
  tz,
  chairs,
  services,
  onClose,
}: {
  series: AppointmentSeries;
  tz: string;
  chairs: Staff[];
  services: Service[];
  onClose: () => void;
}) {
  const edit = useEditSeries();
  const toast = useToast();
  const bookable = useMemo(() => services.filter((s) => s.active), [services]);

  const firstDate = isoDateInZone(new Date(series.firstStartAt), tz);
  const firstTime = clockLabelAt24(series.firstStartAt, tz);

  const [staffId, setStaffId] = useState(series.staffId);
  const [serviceId, setServiceId] = useState(series.serviceId);
  const [clientName, setClientName] = useState(series.clientDisplayName);
  const [nextDate, setNextDate] = useState(firstDate);
  const [time, setTime] = useState(firstTime);
  const [intervalWeeks, setIntervalWeeks] = useState(series.intervalWeeks);
  const [occurrenceCount, setOccurrenceCount] = useState(series.occurrenceCount);
  const [notes, setNotes] = useState(series.notes ?? '');
  const [result, setResult] = useState<EditSeriesResult | null>(null);

  const member = chairs.find((s) => s.id === staffId);
  const service =
    bookable.find((s) => s.id === serviceId) ?? services.find((s) => s.id === serviceId);
  const startMin = parseTime(time);

  const valid =
    Boolean(staffId && serviceId && startMin != null && nextDate) &&
    clientName.trim().length > 0 &&
    occurrenceCount >= 1 &&
    occurrenceCount <= MAX_OCCURRENCES &&
    intervalWeeks >= 1 &&
    intervalWeeks <= MAX_INTERVAL_WEEKS;

  async function submit() {
    if (!valid || startMin == null) {
      toast.error('Check the staff, service, time and cadence.');
      return;
    }
    try {
      const res = await edit.mutateAsync({
        id: series.id,
        body: {
          staffId,
          serviceId,
          firstStartAt: wallMinutesToUtcIso(nextDate, startMin, tz),
          intervalWeeks,
          occurrenceCount,
          notes: notes.trim() || null,
          clientDisplayName: clientName.trim(),
        },
      });
      setResult(res);
      if (res.conflicts.length > 0) {
        toast.error(
          `Series updated — ${res.conflicts.length} future date${res.conflicts.length === 1 ? '' : 's'} couldn’t fit. Review below.`,
        );
      } else {
        toast.success('Series updated — future bookings re-booked.');
      }
    } catch {
      toast.error('Could not update this series.');
    }
  }

  if (result) {
    return (
      <Modal
        open
        onClose={onClose}
        title="Series updated"
        width={520}
        footer={<Button onClick={onClose}>Done</Button>}
      >
        <div className="muted" style={{ fontSize: 13 }}>
          Future bookings were regenerated. Past and in-progress appointments were left untouched.
        </div>
        <SeriesResultPanel
          created={result.created}
          conflicts={result.conflicts}
          tz={tz}
          createdNoun="re-booked"
        />
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit standing booking"
      width={520}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!valid || edit.isPending}>
            {edit.isPending ? 'Updating…' : 'Apply to future bookings'}
          </Button>
        </>
      }
    >
      <div
        className="muted"
        style={{
          fontSize: 12.5,
          background: 'var(--slm-porcelain)',
          border: '1px solid var(--slm-border)',
          borderRadius: 8,
          padding: '8px 10px',
          marginBottom: 8,
        }}
      >
        Changes apply to every <strong>future</strong> booking in this series. Past and in-progress
        appointments are not touched. To change just one date, open that appointment and choose
        “edit this one”.
      </div>

      <Field label="Client">
        <TextInput value={clientName} onChange={(e) => setClientName(e.target.value)} />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="With">
          <Select value={staffId} onChange={(e) => setStaffId(e.target.value)}>
            {chairs.map((s) => (
              <option key={s.id} value={s.id}>
                {s.displayName}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Service"
          hint={
            service ? `${money(service.priceFrom)} · ${serviceSpanMin(service)} min` : undefined
          }
        >
          <Select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
            {bookable.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="section-title" style={{ fontSize: 15, margin: '8px 0 2px' }}>
        Cadence (from the next booking)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Next date">
          <TextInput type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
        </Field>
        <Field label="Time">
          <TextInput type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
        <Field label="Repeats">
          <Select
            value={String(intervalWeeks)}
            onChange={(e) => setIntervalWeeks(Number(e.target.value))}
          >
            {INTERVAL_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Occurrences" hint={`1–${MAX_OCCURRENCES}`}>
          <TextInput
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_OCCURRENCES}
            value={String(occurrenceCount)}
            onChange={(e) => setOccurrenceCount(Number(e.target.value) || 0)}
          />
        </Field>
      </div>

      {valid ? (
        <Pill tone="cobalt">
          {seriesSummary(intervalWeeks, occurrenceCount, service, member?.displayName ?? '')}
        </Pill>
      ) : null}

      <Field label="Notes">
        <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
      </Field>
    </Modal>
  );
}

/** Local helper: the wall-clock "HH:MM" (24h) of an instant in the salon zone, for the time input. */
function clockLabelAt24(iso: string, tz: string): string {
  const total = Math.round(wallMinutes(iso, tz));
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
